import React from 'react';
import { get, cloneDeep, map, find, isEmpty, noop, merge, split, trim, parseInt, toString } from 'lodash';
import { withRouter } from 'react-router-dom';
import { memberDetailsPayBalanceSelectCard as MemberDetailsPayBalanceSelectCardView } from 'ui/views/widgets/memberDetailsPayBalanceSelectCard';
import { MemberDetailsPayBalanceSelectCardService } from 'services/memberDetailsPayBalanceSelectCard';
import queryString from 'query-string';
import moment from 'moment';

import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';
import { SERVICE_RESPONSE_STATUS, SIZES } from 'constants/common';
import * as successOverlay from 'utils/successOverlay';
import { GLOBAL_LABELS } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';

class _memberDetailsPayBalanceSelectCard extends React.Component {

	/**
	 * @param {object} props - props
	 */
	constructor( props ) {
		super( props );

		// create service instance
		this.memberDetailsPayBalanceSelectCardService = new MemberDetailsPayBalanceSelectCardService();

		// bind
		this.handlePaymentOptionSelection = this.handlePaymentOptionSelection.bind( this );
		this.handlePaymentDefaultSelection = this.handlePaymentDefaultSelection.bind( this );
		this.handleActivatePaymentInfo = this.handleActivatePaymentInfo.bind( this );
		this.submitActivatePaymentInfo = this.submitActivatePaymentInfo.bind( this );
		this.handleInactivatePaymentInfo = this.handleInactivatePaymentInfo.bind( this );
		this.handleEditPaymentInfo = this.handleEditPaymentInfo.bind( this );
        this.handleHsaFsaActionToggle = this.handleHsaFsaActionToggle.bind( this );

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			successView: undefined,
		};

		// start data fetch
		this.fetchComponentData();
	}

	/*********************************************/

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'MemberDetailsPayBalanceSelectCard.fetchComponentData()' );

		// cancel ongoing request
		this.memberDetailsPayBalanceSelectCardService.cancel();

		// fetch component data
		const { status, data, } = await this.memberDetailsPayBalanceSelectCardService.fetchComponentData( {
			url: this.props.widget.url,
			data: this.props.requestPayload,
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState(
				{
					isFetching: false,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
					navActions: data.navActions,
					componentData: data,
				},
				() => {

					// get the address list
					const paymentOptions = get( data, 'paymentOptions', [] );

					// get selected address
					const paymentOption = find( paymentOptions, { selected: true } );

					if ( ! isEmpty( paymentOption ) ) {
						this.setState( {
							paymentOption: paymentOption
						}, () => {
							this.props.onPaymentOptionSelect( {
								id: paymentOption.id,
							} );
						} );
					}
				}
			);
		}
	}

	/**
	 * update selection
	 */
	handlePaymentOptionSelection( { card } ) {
		log.debug( 'MemberDetailsPayBalanceSelectCard.handlePaymentOptionSelection()' );

		const componentData = cloneDeep( this.state.componentData );
		const updatedList = map( componentData.paymentOptions, ( item ) => {
			item.selected = item.id === card.id ? true : false;
			return item;
		} );
		componentData.paymentOptions = updatedList;

		this.setState( {
			componentData: componentData,
			paymentOption: card,
		}, () => {
			this.props.onPaymentOptionSelect( {
				id: card.id
			} );
		} );
	}

	/**
	 * update default payment selection
	 */
	async handlePaymentDefaultSelection( { card } ) {
		log.debug( 'MemberDetailsPayBalanceSelectCard.handlePaymentDefaultSelection()' );

		// cancel ongoing request
		this.memberDetailsPayBalanceSelectCardService.cancel();

		//show preloader
		pageLevelPreloader.show();

		const url = find( this.state.componentData.modifyActions, {
			name: 'setDefault',
		} )[ 'url' ];

		// set the address default
		const { status, data } = await this.memberDetailsPayBalanceSelectCardService.setDefaultPaymentInfo( {
			url,
			data: merge( this.props.requestPayload, {
				data: {
					id: card.id,
				}
			} ),
		} );

		//hide preloader
		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

			// update address list
			const componentData = cloneDeep( this.state.componentData );
			const updatedList = map( componentData.paymentOptions, ( item ) => {
				item.flags.isDefault = card.id === item.id ? true : false;
				return item;
			} );
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			}, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
		}
	}

    /**
     * update edit payment info
     */
    async handleEditPaymentInfo( { card, month, year } = {}, callback = noop ) {
        log.debug( 'MemberDetailsPayBalanceSelectCard.handleEditPaymentInfo()' );

        const action = find( this.state.componentData.modifyActions, { name: 'edit' } );
 
        // cancel ongoing request
        this.memberDetailsPayBalanceSelectCardService.cancel();

        //hide preloader
        pageLevelPreloader.show();

        // set the address default
        const { status, data } = await this.memberDetailsPayBalanceSelectCardService.submitEditedPaymentInfo( {
            url: action.url,
            data: merge( this.props.requestPayload, {
                data: {
                    id: card.id,
                    month: month,
                    year: year,
                }
            } ),
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update expiry date
            const componentData = cloneDeep( this.state.componentData );
            const cardItem = find( componentData.paymentOptions, item => {
                return card.id === item.id;
            } );
            cardItem.expDate = `${ month }/${ year }`;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, callback );

        } else {

            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
        
	}

	/**
	 * initiate activate payment info
	 */
	async handleActivatePaymentInfo( { card } ) {
		log.debug( 'MemberDetailsPayBalanceSelectCard.handleActivatePaymentInfo()' );

		let [ expiryMonth, expiryYear ] = split( card.expDate, '/' );
		expiryMonth = parseInt( trim( expiryMonth ) );
		expiryYear = parseInt( trim( expiryYear ) );

		const currentMonth = parseInt( moment().format( 'MM' ) );
		const currentYear = parseInt( moment().format( 'YY' ) );

		if ( expiryYear < currentYear || ( expiryYear === currentYear && expiryMonth < currentMonth ) ) {
			overlay.open( {
				settings: {
					modalType: OVERLAY_VIEWS.PAYMENT_CARD_ACTIVATION,
                    showCloseButton: false,
                    ignoreOutsideClick: false
				},
				data: {
					onActivateActionClick: this.submitActivatePaymentInfo,
					onSubmitEditedDate: this.handleEditPaymentInfo,
					onCancelClick: overlay.close,
                    onHsaFsaActionToggle: this.handleHsaFsaActionToggle,
					actions: this.state.componentData.modifyActions,
					card
				}
			} );
		} else {
			const action = find( this.state.componentData.modifyActions, { name: 'activate' } );
			this.submitActivatePaymentInfo( { url: action.url, id: card.id, month: toString( expiryMonth ), year: toString( expiryYear ) } );
		}
	}

	/**
	 * submit activate payment info
	 */
	async submitActivatePaymentInfo( { url, id, month, year } ) {
		log.debug( 'MemberDetailsPayBalanceSelectCard.submitActivatePaymentInfo()' );

		this.memberDetailsPayBalanceSelectCardService.cancel();

		overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.memberDetailsPayBalanceSelectCardService.submitActivatePaymentInfoRequest( {
			url,
			data: {
				context: { memberId: this.props.requestPayload.context.memberId },
				data: {
					id,
					month,
					year
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isActivated = true;

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			} );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}
	
	/**
	 * inactivate payment info
	 */
	async handleInactivatePaymentInfo( { card } ) {
		log.debug( 'MemberDetailsPayBalanceSelectCard.handleInactivatePaymentInfo()' );

		const action = find( this.state.componentData.modifyActions, {
			name: 'inactivate',
		} );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation.message ),
			iconName: 'close',
			confirmViewConfig: {
				type: 'paymentInfo',
				card: card
			}
		} );

		if ( shouldProceed ) {

			// cancel ongoing request
			this.memberDetailsPayBalanceSelectCardService.cancel();

			//hide preloader
			pageLevelPreloader.show();

			// set the address default
			const { status, data } = await this.memberDetailsPayBalanceSelectCardService.deletePaymentInfo( {
				url: action.url,
				data: {
					context: { memberId: this.props.requestPayload.context.memberId },
					data: { id: card.id }
				}
			} );

			//hide preloader
			pageLevelPreloader.hide();

			if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

				// update payment options
				const componentData = cloneDeep( this.state.componentData );

				const updatedList = map( componentData.paymentOptions, ( item ) => {
					if ( item.id === card.id ) {
						const updatedItem = cloneDeep( item );
						updatedItem.selected = false;
						updatedItem.flags.isActivated = false;
						updatedItem.flags.isDefault = false;

						return updatedItem;
					}

					return item;
				} );
				
				componentData.paymentOptions = updatedList;

				this.setState( {
					componentData,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				}, () => {
					if ( this.props.paymentSelectionId === card.id ) {
						this.props.onPaymentOptionSelect();
					}
				} );
			} else {
				if( ! isEmpty( get( data, 'description' ) ) ) {
					errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
				} else {
					errorOverlay.open();
				}
			}
		}
	}

	// /**
	//  * @desc move to next step and selection data for current step.
	//  */
	// async submitAmountAndPaymentInfo( { url, data } ) {
	// 	log.debug( 'MemberDetailsPayBalanceSelectCard.submitAmountAndPaymentInfo()' );

	// 	this.memberDetailsPayBalanceSelectCardService.cancel();

	// 	// show page level preloader
	// 	pageLevelPreloader.show();

	// 	// get status and service data
	// 	const {
	// 		status,
	// 		successViewData,
	// 	} = await this.memberDetailsPayBalanceSelectCardService.submitAmountAndPaymentInfo(
	// 		{
	// 			url: url,
	// 			data: {
	// 				...this.props.requestPayload,
	// 				data
	// 			},
	// 		}
	// 	);

	// 	// hide page level preloader
	// 	pageLevelPreloader.hide();

	// 	// check if response status was success
	// 	if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
	// 		this.setState( {
	// 			isFetching: false,
	// 			networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
	// 		} );
	// 	} else {
	// 		this.setState( {
	// 			isFetching: false,
	// 			networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
	// 			triggerSuccessView: true,
	// 			successView: successViewData,
	// 		} );
	// 	}
	// }

	// /**
	//  * @desc: action executed when user clicks on any of the navigation actions
	//  */
	// handleNavActionClick( { value } ) {
	// 	log.debug( 'MemberDetailsPayBalanceSelectCard.handleNavActionClick()' );

	// 	if ( 'cancel' === value.name ) {
	// 		this.cancelPayment( { action: value } );
	// 	}

	// 	if ( 'payNow' === value.name ) {

	// 		const data = {
	// 			paymentOption: get( this.state, 'paymentOption.id', null )
	// 		};

	// 		this.submitAmountAndPaymentInfo( {
	// 			url: value.url,
	// 			data,
	// 		} );
	// 	}
	// }

	// //cancel order
    // async cancelPayment( { action } ) {
    //     log.debug( 'MemberDetailsPayBalanceSelectCard.cancelPayment()' );

    //     const shouldProceed = await confirmOverlay.open( {
	// 		message: getLabel( action.confirmation ),
	// 		iconName: 'close'
    //     } );
        
    //     if( shouldProceed ) {

    //         this.memberDetailsPayBalanceSelectCardService.cancel();

    //         // show page level preloader
    //         pageLevelPreloader.show();
    
    //         // get status and service data
    //         const { status } = await this.memberDetailsPayBalanceSelectCardService.cancelPayment( {
    //             url: action.url,
    //             data: this.props.requestPayload
    //         } );
              
    //         // hide page level preloader
    //         pageLevelPreloader.hide();
    
    //         if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
    
    //             overlay.close();
    
    //         } else {
    //             errorOverlay.open(); // show error
    //         }
    //     }
    // }


	/**
	 * submit hsaFsa toggle info
	 */
     async handleHsaFsaActionToggle( { url, id, flags }, callback = noop  ) {
		log.debug( 'MemberDetailsPayBalanceSelectCard.handleHsaFsaActionToggle()' );

		this.memberDetailsPayBalanceSelectCardService.cancel();

		// overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.memberDetailsPayBalanceSelectCardService.submitToggleHsaFsaPaymentInfoRequest( {
			url,
			data: {
				context: { memberId: this.props.requestPayload.context.memberId },
				data: {
					id,
                    isHsaFsa: ! get( flags, 'isHsaFsa', false )
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isHsaFsa = ! get( flags, 'isHsaFsa', false );

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			}, callback );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}


	/*********************************************/

	/**
	 * @desc When component unmounts, cleanup resouces.
	 */
	componentWillUnmount() {
		this.memberDetailsPayBalanceSelectCardService.cancel(); // cancel ongoing requests
	}

	/**
	 * @desc Render a React view component
	 */
	render() {

        // construct render redirection path for add payment
        const addPaymentRedirectPath = queryString.stringifyUrl( {
            url: this.props.location.pathname,
            query: {
            }
		} );
		
		return (
			<MemberDetailsPayBalanceSelectCardView
				isFetching={this.state.isFetching}
				networkStatus={this.state.networkStatus}
				{...get( this.state, 'componentData', {} )}
				requestPayload={ this.props.requestPayload }
				
                // isPaymentRequired={ this.props.isPaymentRequired }
                onPaymentOptionSelection={ this.handlePaymentOptionSelection }
                onPaymentDefaultSelection={ this.handlePaymentDefaultSelection }
                onPaymemntInfoEdit = { this.handleEditPaymentInfo }
				onPaymemntInfoActivate={ this.handleActivatePaymentInfo }
                onPaymemntInfoInactivate = { this.handleInactivatePaymentInfo }
                onHsaFsaActionToggle = { this.handleHsaFsaActionToggle }
                addPaymentRedirectPath={ addPaymentRedirectPath }
				navActions={this.state.navActions}
				onNavActionClick={this.handleNavActionClick}
				triggerSuccessView={this.state.triggerSuccessView}
				successView={this.state.successView}
				canMoveForward={true}
			/>
		);
	}
}

// set display name
_memberDetailsPayBalanceSelectCard.displayName = 'MemberDetailsPayBalanceSelectCard';

// set default props
_memberDetailsPayBalanceSelectCard.defaultProps = {
	requestPayload: {},
	triggerSuccessView: false,
	onPaymentOptionSelect: noop,
};

// prop types
_memberDetailsPayBalanceSelectCard.propTypes = {};

export const MemberDetailsPayBalanceSelectCard = withRouter(
	_memberDetailsPayBalanceSelectCard
);
